import React, { useState, useRef, useEffect } from 'react';
import { AtSign, ArrowUp, LoaderCircle, Star, Image as ImageIcon, X, Eye, MessageSquareOff, Lock, Crown } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useSonas } from '../SonasContext';
import { AI_MODELS } from '../mockData';
import { useAuth } from '@/integrations/supabase/AuthProvider';
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from 'sonner';

const ChatInput = ({ onSend, isThinking }) => {
  const { tokens = 0, setIsPossessionsOpen, isChatRestricted, isImageRestricted, setIsStoreOpen } = useSonas();
  const { profile } = useAuth();
  const [input, setInput] = useState("");
  const [modelId, setModelId] = useState("nova-fast");
  const [image, setImage] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const isPro = profile?.is_pro === true;
  const selectedModel = AI_MODELS.find(m => m.id === modelId) || AI_MODELS[0];

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 180)}px`;
    }
  }, [input]);

  const handleImageUpload = (e) => {
    if (!isPro) {
      toast.error("Image uploads require Architect Pro.");
      setIsStoreOpen(true);
      return;
    }
    if (isImageRestricted) {
      toast.error("Image uploads restricted by administrator.");
      return;
    }
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSend = (e) => {
    e?.preventDefault?.();
    if (isChatRestricted) {
      toast.error("Building assistant disabled.");
      return;
    }

    if (selectedModel.isProOnly && !isPro) {
      toast.error(`${selectedModel.name} requires Architect Pro.`);
      setIsStoreOpen(true);
      return;
    }

    const trimmedInput = (input ?? "").trim();
    if ((!trimmedInput && !image) || isThinking || tokens <= 0) return;
    
    onSend?.(trimmedInput, { model: modelId, image });
    setInput("");
    setImage(null);
  };

  return (
    <div className="p-4 md:p-6 pt-0">
      {image && (
        <div className="mb-3 flex items-center gap-2 p-2 bg-white/[0.03] border border-white/5 rounded-2xl w-fit relative">
          <img src={image} alt="Preview" className="h-12 w-12 md:h-16 md:w-16 object-cover rounded-xl" />
          <button onClick={() => setImage(null)} className="absolute -top-2 -right-2 h-5 w-5 md:h-6 md:w-6 bg-red-500 rounded-full flex items-center justify-center text-white"><X size={10} /></button>
        </div>
      )}

      <div className={cn("bg-white/[0.03] backdrop-blur-3xl rounded-[24px] md:rounded-[28px] border border-white/[0.05] p-2 md:p-2.5 transition-all shadow-2xl", isChatRestricted && "opacity-50 grayscale pointer-events-none")}>
        <textarea
          ref={textareaRef}
          disabled={isChatRestricted}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={isChatRestricted ? "Assistant disabled." : (selectedModel.hasVision ? "Upload image or describe..." : "How can Sonas help build today?")}
          className="w-full bg-transparent border-none focus:ring-0 resize-none text-sm md:text-[15px] p-2 md:p-3 min-h-[44px] md:min-h-[52px] max-h-[120px] md:max-h-[180px] sonas-scrollbar font-sans outline-none placeholder:text-sonas-dim"
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend(e)}
        />
        
        <div className="flex items-center justify-between border-t border-white/[0.05] mt-1 pt-2 md:pt-3 px-1 md:px-2 pb-1">
          <div className="flex items-center gap-0.5 md:gap-1">
            <button onClick={() => setIsPossessionsOpen(true)} className="h-8 w-8 md:h-9 md:w-9 rounded-lg md:rounded-xl flex items-center justify-center hover:bg-white/5 text-sonas-dim hover:text-sonas-accent" title="Reference Possession"><AtSign className="h-4 w-4 md:h-4.5 md:w-4.5" /></button>
            {!isImageRestricted && (
              <>
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
                <button 
                  onClick={() => fileInputRef.current?.click()} 
                  className={cn("h-8 w-8 md:h-9 md:w-9 rounded-lg md:rounded-xl flex items-center justify-center hover:bg-white/5 text-sonas-dim hover:text-sonas-accent", !isPro && "opacity-50")}
                  title={isPro ? "Upload Reference Image" : "Image Upload (Pro Only)"}
                >
                  <ImageIcon className="h-4 w-4 md:h-4.5 md:w-4.5" />
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <Select value={modelId} onValueChange={setModelId}>
              <SelectTrigger className="h-8 md:h-9 px-3 md:px-4 text-[10px] md:text-[11px] font-bold bg-white/[0.03] border-white/5 w-auto min-w-[100px] md:min-w-[140px] rounded-lg md:rounded-xl gap-2 flex items-center shadow-none">
                {selectedModel.imageUrl ? (
                  <img src={selectedModel.imageUrl} alt={selectedModel.name} className="h-3.5 w-3.5 md:h-4 md:w-4 rounded-sm object-contain" />
                ) : (
                  <selectedModel.icon className={cn("h-3.5 w-3.5 md:h-4 md:w-4", selectedModel.color)} />
                )}
                <span className="truncate max-w-[60px] md:max-w-none">{selectedModel.name}</span>
              </SelectTrigger>
              <SelectContent className="glass-surface border-white/[0.05] w-[260px] md:w-[300px] p-1.5 md:p-2 rounded-2xl">
                {AI_MODELS.map((m) => (
                  <SelectItem key={m.id} value={m.id} className="rounded-xl py-2 md:py-3 cursor-pointer">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className={cn("h-8 w-8 md:h-9 md:w-9 rounded-xl flex items-center justify-center bg-black/40", m.glow)}>
                          {m.imageUrl ? (
                            <img src={m.imageUrl} alt={m.name} className="h-4 w-4 md:h-5 md:w-5 rounded-sm object-contain" />
                          ) : (
                            <m.icon className={cn("h-4 w-4 md:h-4.5 md:w-4.5", m.color)} />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs md:text-[13px] font-bold">{m.name}</span>
                          <span className="text-[8px] md:text-[9px] text-sonas-dim uppercase tracking-tighter">{m.provider}</span>
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button size="icon" className={cn("h-8 w-8 md:h-9 md:w-9 rounded-lg md:rounded-xl transition-all", ((!input.trim() && !image) || isThinking || tokens <= 0 || isChatRestricted) ? "bg-white/5 text-white/10" : "bg-sonas-accent text-sonas-bg shadow-lg shadow-sonas-accent/20")} onClick={handleSend} disabled={isThinking || isChatRestricted}>
              {isThinking ? <LoaderCircle className="h-4 w-4 md:h-5 md:w-5 animate-spin" /> : (isChatRestricted ? <MessageSquareOff size={16} /> : <ArrowUp className="h-4 w-4 md:h-5 md:w-5" />)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;